import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc, updateDoc, increment } from "firebase/firestore";
import { db } from "../lib/firebase";
import { Play, ChevronLeft, Eye, User, Share2, Music } from "lucide-react";

export default function ContentPage() {
  const { contentId } = useParams();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadContent(); }, [contentId]);

  const loadContent = async () => {
    try {
      const contentDoc = await getDoc(doc(db, "content", contentId));
      if (!contentDoc.exists()) { setLoading(false); return; }
      const data = { id: contentDoc.id, ...contentDoc.data() };
      setContent(data);
      // Increment views
      await updateDoc(doc(db, "content", contentId), { views: increment(1) });
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-[#00ff77] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!content) return (
    <div className="text-center py-20">
      <p className="text-zinc-500">Content not found.</p>
      <Link to="/" className="text-[#00ff77] text-sm mt-2 inline-block">← Go Home</Link>
    </div>
  );

  return (
    <div data-testid="content-page" className="space-y-6 animate-fade-in max-w-4xl mx-auto">
      <Link to="/" className="flex items-center gap-1 text-zinc-500 hover:text-white text-sm transition-colors">
        <ChevronLeft size={16} /> Back
      </Link>

      {/* Video player or media */}
      <div className="rounded-2xl overflow-hidden bg-[#111] border border-white/5">
        {content.videoURL ? (
          <video
            data-testid="content-video"
            src={content.videoURL}
            controls
            poster={content.thumbnailURL}
            className="w-full aspect-video"
          />
        ) : content.storageURL ? (
          <div className="relative">
            {content.thumbnailURL && (
              <img src={content.thumbnailURL} alt={content.title} className="w-full aspect-video object-cover" />
            )}
            {content.type === "audio" && (
              <div className="p-4">
                <audio src={content.storageURL} controls className="w-full" />
              </div>
            )}
          </div>
        ) : (
          <div className="aspect-video flex items-center justify-center">
            {content.thumbnailURL ? (
              <img src={content.thumbnailURL} alt={content.title} className="w-full h-full object-cover" />
            ) : (
              <Play size={48} className="text-zinc-600" />
            )}
          </div>
        )}
      </div>

      {/* Info */}
      <div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white mb-1">{content.title}</h1>
            <div className="flex items-center gap-4 text-zinc-500 text-sm">
              <span className="flex items-center gap-1"><Eye size={13} /> {(content.views || 0) + 1} views</span>
              {content.category && <span className="bg-white/5 px-2 py-0.5 rounded-full text-xs">{content.category}</span>}
            </div>
          </div>
          <button
            data-testid="share-btn"
            onClick={() => navigator.share?.({ title: content.title, url: window.location.href })}
            className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white transition-colors"
          >
            <Share2 size={16} />
          </button>
        </div>

        {/* Creator */}
        {content.creatorSlug && (
          <Link
            to={`/c/${content.creatorSlug}`}
            data-testid="content-creator-link"
            className="flex items-center gap-3 mt-4 p-3 bg-[#111] border border-white/5 rounded-xl hover:border-[#00ff77]/20 transition-colors group"
          >
            <div className="w-10 h-10 rounded-full bg-[#1a1a1a] overflow-hidden">
              {content.creatorPhotoURL ? (
                <img src={content.creatorPhotoURL} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User size={16} className="text-zinc-600" />
                </div>
              )}
            </div>
            <div>
              <p className="text-white text-sm font-semibold group-hover:text-[#00ff77] transition-colors">{content.creatorName}</p>
              <p className="text-zinc-500 text-xs">View Profile</p>
            </div>
          </Link>
        )}

        {content.description && (
          <div className="mt-4 bg-[#111] border border-white/5 rounded-xl p-4">
            <p className="text-zinc-300 text-sm leading-relaxed">{content.description}</p>
          </div>
        )}
      </div>
    </div>
  );
}
